/**
 * Gemini Model Constants — Frontend Single Source of Truth
 *
 * GOVERNANCE: Google Gemini is the ONLY allowed AI provider.
 * OpenAI, Anthropic, and other providers are FORBIDDEN.
 *
 * This file is the frontend equivalent of:
 *   supabase/functions/_shared/config/gemini-config.ts
 *
 * When a model is retired, update BOTH this file AND gemini-config.ts.
 * All other frontend files MUST import from here — never hardcode model strings.
 */

// ---------------------------------------------------------------------------
// Model name constants
// ---------------------------------------------------------------------------

export const GEMINI_MODEL_FLASH = 'gemini-2.5-flash' as const;
export const GEMINI_MODEL_PRO = 'gemini-2.5-pro' as const;
export const GEMINI_MODEL_LITE = 'gemini-2.5-flash-lite' as const;

/** Default model used when no explicit model is specified */
export const DEFAULT_GEMINI_MODEL = GEMINI_MODEL_FLASH;

// ---------------------------------------------------------------------------
// Type
// ---------------------------------------------------------------------------

export type GeminiModelId =
  | typeof GEMINI_MODEL_FLASH
  | typeof GEMINI_MODEL_PRO
  | typeof GEMINI_MODEL_LITE;

// ---------------------------------------------------------------------------
// Static model registry (mirrors gemini-config.ts GEMINI_MODELS)
// ---------------------------------------------------------------------------

export interface GeminiModelMeta {
  value: GeminiModelId;
  label: string;
  description: string;
  tier: 'standard' | 'premium' | 'lite';
  isDefault: boolean;
  /** Intra-provider fallback when this model is unavailable */
  fallback: GeminiModelId;
  costPer1MTokens: { input: number; output: number };
}

/** Ordered array — first entry is the default model */
export const GEMINI_MODEL_LIST: GeminiModelMeta[] = [
  {
    value: GEMINI_MODEL_FLASH,
    label: 'Gemini 2.5 Flash',
    description: 'Fast, balanced model for most tasks',
    tier: 'standard',
    isDefault: true,
    fallback: GEMINI_MODEL_PRO,
    costPer1MTokens: { input: 0.075, output: 0.30 },
  },
  {
    value: GEMINI_MODEL_PRO,
    label: 'Gemini 2.5 Pro',
    description: 'Most capable model for complex reasoning',
    tier: 'premium',
    isDefault: false,
    fallback: GEMINI_MODEL_FLASH,
    costPer1MTokens: { input: 0.15, output: 0.60 },
  },
  {
    value: GEMINI_MODEL_LITE,
    label: 'Gemini 2.5 Flash Lite',
    description: 'Fastest, most cost-effective for simple tasks',
    tier: 'lite',
    isDefault: false,
    fallback: GEMINI_MODEL_FLASH,
    costPer1MTokens: { input: 0.03, output: 0.12 },
  },
];

// ---------------------------------------------------------------------------
// Legacy alias map (mirrors gemini-config.ts LEGACY_ALIAS_MAP)
// ---------------------------------------------------------------------------

/** Maps old semantic aliases to current model ids */
export const GEMINI_LEGACY_ALIAS_MAP: Record<string, GeminiModelId> = {
  'gemini-flash-latest': GEMINI_MODEL_FLASH,
  'gemini-pro-latest': GEMINI_MODEL_PRO,
  'gemini-lite-latest': GEMINI_MODEL_LITE,
  'gemini-vision-latest': GEMINI_MODEL_FLASH,
  // GPT aliases — governance violations resolved to Gemini equivalents
  'gpt-latest': GEMINI_MODEL_FLASH,
  'gpt-mini-latest': GEMINI_MODEL_LITE,
  'gpt-embed-latest': GEMINI_MODEL_FLASH,
};

// ---------------------------------------------------------------------------
// Intra-provider fallback map (mirrors gemini-config.ts fallback fields)
// ---------------------------------------------------------------------------

export const GEMINI_FALLBACK_MAP: Record<GeminiModelId, GeminiModelId> = {
  [GEMINI_MODEL_FLASH]: GEMINI_MODEL_PRO,
  [GEMINI_MODEL_PRO]: GEMINI_MODEL_FLASH,
  [GEMINI_MODEL_LITE]: GEMINI_MODEL_FLASH,
};
